import React, {useEffect, useState } from "react";
import {
  EditPageContainer,
  Page,
  withServerErrors,
  required,
} from "components/common";
import { FormGroup, SaveButton } from "components/common/Forms";
import { compose, withState, withHandlers, lifecycle } from "recompose";
import qs from "query-string";
import { LocalForm, Control, actions } from "react-redux-form";
import ConnectionStatus from "./ConnectionStatus";
import { Trans } from "react-i18next";
import Dropzone from "react-dropzone";
import { useUserContext, useOrganisationContext } from "../../hooks";
import BackButton from "components/refactoring/BackButton";
import {Button} from "reactstrap";

import TipSequence from "components/Tips/TipSequence";
import { useTips } from "components/Tips/TipContext";

const tips = [
    {
        id: 'connect-to-xero',
        text: 'Connect to Xero by clicking on the button.',
        targetId: 'connect-to-xero',
        position: 'bottom',
        delay: 500,
        btnText: 'Finish',
    },
];

export const UserEdit = ({
  t,
  client,
  mode,
  item,
  form,
  setDispatch,
  onSubmit: onSubmitProp,
  onUpdate,
  onChange,
  renderServerError,
  readOnly,
  onDisconnected,
  onDropAccepted,
  onLogoLoad,
  onLogoError,
  Errors,
  dispatch
}) => {
  const { updateShowXeroTipsStatus } = useTips();
  const {
    isInTrialPeriod,
  } = useUserContext();

  const { organisationsQuery, selectedOrganisation } = useOrganisationContext();

  const [subscribedFeatures, setSubscribedFeatures] = useState({
    isAccounting: isInTrialPeriod,
    isWPConnect: isInTrialPeriod,
    isPayroll: isInTrialPeriod,
    isActive: isInTrialPeriod,
  })

  useEffect(() => {
    let tip = tips.find(tip => tip.id === 'connect-to-xero')

    if (tip) {
      tip.onComplete = updateShowXeroTipsStatus
    }
  }, [])

  useEffect(() => {
    if (!isInTrialPeriod && selectedOrganisation?.subscribed_features) { 
      let subFeatures = selectedOrganisation?.subscribed_features
      let accounting = subFeatures?.hesaabmodule?.includes('accounting')
      let wpconnect = subFeatures?.hesaabmodule?.includes('wpconnect')
      let payroll = subFeatures?.hesaabmodule?.includes('payroll')
      let active = accounting || wpconnect || payroll

      setSubscribedFeatures({
        isAccounting: accounting,
        isWPConnect: wpconnect,
        isPayroll: payroll,
        isActive: active,
      })
    }

  }, [selectedOrganisation, ])

  const {
    isActive
  } = subscribedFeatures

  async function onSubmit(...args) {
    await onSubmitProp(...args);
    await organisationsQuery.refetch();
  }

  const apiUrl = 'api/'; 
  const parts = item?.logo?.split('://');
  const newUrlImage = item?.logo

  function handleLogoClear() {
    dispatch(actions.change('local.logo', undefined))
  }

  return (
    <Page title={`${mode} Organisation`}>
      <BackButton/>
      <LocalForm
        initialState={item}
        onSubmit={onSubmit}
        getDispatch={setDispatch}
        onUpdate={onUpdate}
        onChange={onChange}
        validators={{
          name: { required },
        }}
      >
        <FormGroup label="Name *" model=".name">
          <Control.text model=".name" />
          <p className="form-text text-muted small">
            <Trans>
              A descriptive name for this organisation, it does not need to be
              the same as the organisation name in Xero
            </Trans>
          </p>
        </FormGroup>
        <FormGroup label="Logo" model=".logo">
          <Dropzone
            multiple={false}
            className="border border-primary rounded"
            style={{}}
            onDropAccepted={onDropAccepted}
          >
            <div className="m-2 text-center">
              {item.logo ? (
                <div>
                  <img 
                    alt="logo"
                    onLoad={onLogoLoad}
                    onError={onLogoError}
                    className="img-thumbnail mb-3"
                    src={newUrlImage}
                    style={{ maxHeight: "200px" }}
                  />
                </div>
              ) : null}
              <span>
                <Trans>
                  Drop an image file here, or click to select a file to upload.
                </Trans>
              </span>
            </div>
          </Dropzone>
          {item.logo ? (
            <div className="tw-flex tw-justify-end mt-2">
              <Button onClick={handleLogoClear} size="sm">
                <Trans>
                  Clear
                </Trans>
              </Button>
            </div>
          ) : null}
        </FormGroup>
        {renderServerError("name")}
        {item.id && (
          <FormGroup label="Status">
            <div className="form-text">
              <ConnectionStatus
                item={item}
                showDisconnect
                onDisconnected={onDisconnected}
              />
            </div>
          </FormGroup>
        )}
        {isActive && item.id === selectedOrganisation?.id && item.status === "NOT_CONNECTED" && item.xero_connect_url && (
          <a
            href={item.xero_connect_url + `&organisation=${item.id}`}
            className="btn btn-primary ml-0"
            id="connect-to-xero"
          >
            <Trans>Connect to Xero</Trans>
          </a>
        )}
        <FormGroup className="my-3 d-flex justify-content-end">
          <SaveButton className="px-3 m-0" form={form} hidden={readOnly} />
        </FormGroup>

      </LocalForm>

      {isActive && item.id === selectedOrganisation?.id && item.status === "NOT_CONNECTED" && item.xero_connect_url && <TipSequence tips={tips} />}
    </Page>
  );
};

const enhance = compose(
  withServerErrors(),
  EditPageContainer({
    url: "connected_organisations",
    defaultItem: { name: "", logo: undefined },
  }),
  withState("serverErrors", "setServerErrors", {}),
  withState("redirectUrl", "setRedirectUrl", null),
  withHandlers({
    onDropAccepted:
      ({ dispatch, setFile }) =>
        ([file]) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            dispatch(actions.change("local.logo", e.target.result));
          };
          reader.readAsDataURL(file);
        },
    onLogoError:
      ({ dispatch }) =>
        () => {
          dispatch(actions.setErrors("local.logo", { invalid: true }));
        },
    onLogoLoad:
      ({ dispatch }) =>
        (e) => {
          dispatch(actions.setErrors("local.logo", { invalid: false }));
        },
    onDisconnected:
      ({ dispatch }) =>
        () => {
          dispatch(actions.change("local.status", "NOT_CONNECTED"));
        },
  }),
  lifecycle({
    componentDidMount() {
      Promise.resolve().then(() => {
        const q = qs.parse(this.props.location.search);
        if (q.oauth_verifier && q.oauth_token && q.org)
          this.props.verify(q.oauth_token, q.oauth_verifier, q.org);
      });
    },
  })
);

export default enhance(UserEdit);
